
import {
  getQuoteDetail,
  updateQuote,
  linkQuoteToJob,
  emailQuote,
  downloadQuotePdf,
  uploadQuoteFile,
  deleteQuoteFile,
} from '@/api';
import BackButton from '@/components/UI/backButton/BackButton.vue';
import Button from '@/components/UI/button/Button.vue';
import DropZone from '@/components/UI/DropZone.vue';
import * as routeNames from '@/router/routeNames';
import * as Sentry from '@sentry/vue';

import {
  QuoteDetail as QuoteDetailType,
  QuoteStopCost,
  QuoteCalculateRequest,
  QuoteItem,
  LegacyQuoteSourceLine,
} from '@/models/quote';
import { format, parseISO } from 'date-fns';
import { Component, Vue } from 'vue-property-decorator';
import { notificationErrorText } from '@/utils/PortalConstants';

@Component({
  components: {
    BackButton,
    Button,
    DropZone,
  },
})
export default class QuoteDetail extends Vue {
  private loading = false;
  private updating = false;
  private emailing = false;
  private linking = false;
  private downloadingPdf = false;
  private quote: QuoteDetailType | null = null;

  // Editable fields
  private editReference = '';
  private editNotes = '';

  // File uploads
  private uploading = false;
  private deletingFileUuid = '';
  private uploadError = '';
  private readonly allowedFileFormats = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/heic',
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ];

  // Dialogs
  private showCancelDialog = false;
  private showLinkJobDialog = false;
  private linkJobId: number | null = null;
  private showEmailDialog = false;
  private emailTo = '';
  private dialogError = '';

  get isEditable(): boolean {
    return this.quote?.status === 'draft';
  }

  get canUploadFiles(): boolean {
    return this.quote?.status === 'draft' || this.quote?.status === 'sent';
  }

  get availableTransitions(): {
    value: string;
    label: string;
    icon: string;
    color: string;
  }[] {
    if (!this.quote) {
      return [];
    }
    const transitionMap: Record<string, { value: string; label: string; icon: string; color: string }[]> = {
      draft: [
        {
          value: 'accepted',
          label: 'Mark as Accepted',
          icon: 'mdi-check-circle',
          color: 'green',
        },
        {
          value: 'cancelled',
          label: 'Cancel Quote',
          icon: 'mdi-close-circle',
          color: 'red',
        },
      ],
      sent: [
        {
          value: 'accepted',
          label: 'Mark as Accepted',
          icon: 'mdi-check-circle',
          color: 'green',
        },
        {
          value: 'cancelled',
          label: 'Cancel Quote',
          icon: 'mdi-close-circle',
          color: 'red',
        },
      ],
      accepted: [
        {
          value: 'cancelled',
          label: 'Cancel Quote',
          icon: 'mdi-close-circle',
          color: 'red',
        },
      ],
    };
    return transitionMap[this.quote.status] || [];
  }

  get sourceObject(): QuoteCalculateRequest | null {
    const sd = this.quote?.source_data;
    if (!sd || Array.isArray(sd)) {
      return null;
    }
    return sd as QuoteCalculateRequest;
  }

  get legacyLines(): LegacyQuoteSourceLine[] {
    const sd = this.quote?.source_data;
    return Array.isArray(sd) ? (sd as LegacyQuoteSourceLine[]) : [];
  }

  get isLegacySource(): boolean {
    return this.legacyLines.length > 0;
  }

  get hasOriginalRequest(): boolean {
    return !!this.sourceObject || this.isLegacySource;
  }

  get displayItems(): QuoteItem[] {
    return this.sourceObject?.items ?? [];
  }

  get displayJobType(): string {
    if (this.sourceObject) {
      return this.sourceObject.job_type;
    }
    if (this.isLegacySource) {
      return this.legacyLines[0]?.job_type ?? '';
    }
    return '';
  }

  get displaySurcharges(): string[] {
    if (this.sourceObject) {
      return this.sourceObject.surcharges ?? [];
    }
    if (this.isLegacySource) {
      const set = new Set<string>();
      for (const l of this.legacyLines) {
        (l.surcharges ?? []).forEach(s => set.add(s));
      }
      return Array.from(set);
    }
    return [];
  }

  get displayFrom(): { suburb: string; postcode: string } | null {
    const o = this.sourceObject;
    if (o?.from_suburb) {
      return { suburb: o.from_suburb, postcode: o.from_postcode ?? '' };
    }
    const l = this.legacyLines[0];
    if (l?.from_suburb) {
      return { suburb: l.from_suburb, postcode: l.from_postcode ?? '' };
    }
    return null;
  }

  get displayTo(): { suburb: string; postcode: string } | null {
    const o = this.sourceObject;
    if (o?.to_suburb) {
      return { suburb: o.to_suburb, postcode: o.to_postcode ?? '' };
    }
    const l = this.legacyLines[0];
    if (l?.to_suburb) {
      return { suburb: l.to_suburb, postcode: l.to_postcode ?? '' };
    }
    return null;
  }

  get isMetro(): boolean {
    const f = this.displayFrom;
    const t = this.displayTo;
    if (!f && !t) {
      return true;
    }
    if (f && t) {
      return f.suburb === t.suburb && f.postcode === t.postcode;
    }
    return true;
  }

  get allCosts(): QuoteStopCost[] {
    if (!this.quote?.stops) {
      return [];
    }
    const costs: QuoteStopCost[] = [];
    for (const stop of this.quote.stops) {
      costs.push(...stop.costs);
    }
    return costs;
  }

  get hasFuelLevy(): boolean {
    if (!this.quote) {
      return false;
    }
    return parseFloat(this.quote.fuel_levy_ex_gst || '0') > 0;
  }

  get subtotalExGst(): string {
    return (parseFloat(this.quote?.total_fee_ex_gst || '0') - parseFloat(this.quote?.fuel_levy_ex_gst || '0')).toFixed(
      2,
    );
  }

  get subtotalGst(): string {
    return (parseFloat(this.quote?.total_gst || '0') - parseFloat(this.quote?.fuel_levy_gst || '0')).toFixed(2);
  }

  get subtotalIncGst(): string {
    return (
      parseFloat(this.quote?.total_fee_inc_gst || '0') - parseFloat(this.quote?.fuel_levy_inc_gst || '0')
    ).toFixed(2);
  }

  async mounted() {
    const id = parseInt(this.$route.params.id, 10);
    if (!id) {
      this.$router.push({ name: routeNames.LIST_QUOTES });
      return;
    }
    await this.fetchQuote(id);
  }

  private async fetchQuote(id: number) {
    this.loading = true;
    try {
      this.quote = await getQuoteDetail(id);
      this.editReference = this.quote.reference || '';
      this.editNotes = this.quote.notes || '';
    } catch (err: any) {
      Sentry.captureException(err);
      this.$notify({
        group: 'network',
        text: notificationErrorText,
        type: 'warn',
        duration: 3000,
      });
    } finally {
      this.loading = false;
    }
  }

  private formatDate(dateStr: string): string {
    try {
      return format(parseISO(dateStr), 'dd MMM yyyy HH:mm');
    } catch {
      return dateStr || '';
    }
  }

  private statusColor(status: string): string {
    const colors: Record<string, string> = {
      draft: 'grey',
      sent: 'blue',
      accepted: 'green',
      booked: 'purple',
      expired: 'orange',
      cancelled: 'red',
    };
    return colors[status] || 'grey';
  }

  private capitalize(str: string): string {
    return str ? str.charAt(0).toUpperCase() + str.slice(1) : '';
  }

  // Save reference/notes (draft only)
  private async saveChanges() {
    if (!this.quote) {
      return;
    }
    this.updating = true;
    try {
      this.quote = await updateQuote(this.quote.id, {
        reference: this.editReference,
        notes: this.editNotes,
      });
      this.$notify({
        group: 'network',
        text: 'Quote updated.',
        type: 'success',
        duration: 3000,
      });
    } catch (err: any) {
      this.notifyError(err);
    } finally {
      this.updating = false;
    }
  }

  // POST /api/quote/{id}/email/ — renders PDF, sends email, sets status=sent
  private async sendEmail() {
    if (!this.quote || !this.emailTo) {
      return;
    }
    this.emailing = true;
    this.dialogError = '';
    try {
      // Save any pending edits first
      if (this.isEditable) {
        await updateQuote(this.quote.id, {
          reference: this.editReference,
          notes: this.editNotes,
        });
      }
      this.quote = await emailQuote(this.quote.id, this.emailTo);
      this.showEmailDialog = false;
      this.dialogError = '';
      this.$notify({
        group: 'network',
        text: `Quote emailed to ${this.emailTo}`,
        type: 'success',
        duration: 3000,
      });
    } catch (err: any) {
      this.dialogError = this.extractErrorMessage(err);
    } finally {
      this.emailing = false;
    }
  }

  // GET /api/quote/{id}/pdf/ — download PDF
  private async downloadPdf() {
    if (!this.quote) {
      return;
    }
    this.downloadingPdf = true;
    try {
      const blob = await downloadQuotePdf(this.quote.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `quote-${this.quote.id}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      this.notifyError(err);
    } finally {
      this.downloadingPdf = false;
    }
  }

  // PATCH status (accepted, cancelled, expired)
  private async updateStatus(status: string) {
    if (!this.quote) {
      return;
    }
    this.showCancelDialog = false;
    this.updating = true;
    try {
      this.quote = await updateQuote(this.quote.id, { status: status as any });
      this.$notify({
        group: 'network',
        text: `Quote ${this.capitalize(status)}.`,
        type: 'success',
        duration: 3000,
      });
    } catch (err: any) {
      this.notifyError(err);
    } finally {
      this.updating = false;
    }
  }

  private onStatusTransition(status: string) {
    if (status === 'cancelled') {
      this.confirmCancel();
    } else {
      this.updateStatus(status);
    }
  }

  private confirmCancel() {
    this.showCancelDialog = true;
  }

  private async linkJob() {
    if (!this.quote || !this.linkJobId) {
      return;
    }
    this.linking = true;
    this.dialogError = '';
    try {
      await linkQuoteToJob(this.quote.id, this.linkJobId);
      this.showLinkJobDialog = false;
      this.dialogError = '';
      await this.fetchQuote(this.quote.id);
      this.$notify({
        group: 'network',
        text: 'Quote linked to job.',
        type: 'success',
        duration: 3000,
      });
    } catch (err: any) {
      this.dialogError = this.extractErrorMessage(err);
    } finally {
      this.linking = false;
    }
  }

  private closeEmailDialog() {
    this.showEmailDialog = false;
    this.dialogError = '';
  }

  private closeLinkJobDialog() {
    this.showLinkJobDialog = false;
    this.dialogError = '';
  }

  private goToJob() {
    if (this.quote?.job_id) {
      this.$router.push({ path: `/job/${this.quote.job_id}` });
    }
  }

  // File helpers
  private fileIcon(contentType: string): string {
    if (contentType.startsWith('image/')) {
      return 'mdi-file-image';
    }
    if (contentType === 'application/pdf') {
      return 'mdi-file-pdf-box';
    }
    return 'mdi-file-document';
  }

  private fileDownloadUrl(uuid: string): string {
    return `${process.env.VUE_APP_API}/quote/files/${uuid}/download/`;
  }

  private formatFileSize(bytes: number): string {
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }

  private async onFilesAdded(files: File[]) {
    if (!this.quote || files.length === 0) {
      return;
    }
    const file = files[0];
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      this.uploadError = 'File exceeds the 10 MB limit.';
      return;
    }
    this.uploading = true;
    this.uploadError = '';
    try {
      const uploaded = await uploadQuoteFile(this.quote.id, file);
      this.quote.files.push(uploaded);
      this.$notify({
        group: 'network',
        text: `File "${uploaded.original_filename}" uploaded.`,
        type: 'success',
        duration: 3000,
      });
    } catch (err: any) {
      if (err?.response?.status === 400 && err.response.data?.error) {
        this.uploadError = err.response.data.error;
      } else {
        this.uploadError = 'Upload failed.';
        Sentry.captureException(err);
      }
    } finally {
      this.uploading = false;
    }
  }

  private async deleteFile(uuid: string) {
    if (!this.quote) {
      return;
    }
    this.deletingFileUuid = uuid;
    try {
      await deleteQuoteFile(this.quote.id, uuid);
      this.quote.files = this.quote.files.filter(f => f.uuid !== uuid);
      this.$notify({
        group: 'network',
        text: 'File deleted.',
        type: 'success',
        duration: 3000,
      });
    } catch (err: any) {
      this.notifyError(err);
    } finally {
      this.deletingFileUuid = '';
    }
  }

  private extractErrorMessage(err: any): string {
    if (err?.response?.status === 400) {
      const data = err.response.data;
      if (data?.error) {
        return data.error;
      }
      if (data?.email_to) {
        return data.email_to.join(', ');
      }
      return 'Operation failed.';
    }
    if (err?.response?.status === 404) {
      return 'Not found.';
    }
    if (err?.response?.status === 500) {
      return err.response.data?.error || 'Server error.';
    }
    Sentry.captureException(err);
    return notificationErrorText;
  }

  private notifyError(err: any) {
    const msg = this.extractErrorMessage(err);
    this.$notify({ group: 'network', text: msg, type: 'warn', duration: 5000 });
  }
}
